import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FaUser, FaEnvelope, FaGraduationCap, FaInfoCircle, FaCheckCircle, FaTimes } from 'react-icons/fa';
import { enquiryAPI } from '../../services/api';

const PublicEnquiryForm = () => {
  const { qrCodeId } = useParams();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [qrValid, setQrValid] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    interested_course: '',
    preferred_batch_timing: '',
    how_did_you_hear: '',
    additional_notes: ''
  });

  const courseOptions = [
    { value: '', label: 'Select a course' },
    { value: 'Python Programming', label: 'Python Programming' },
    { value: 'Data Science', label: 'Data Science' },
    { value: 'Machine Learning', label: 'Machine Learning' },
    { value: 'Artificial Intelligence', label: 'Artificial Intelligence' }
  ];

  const timingOptions = [
    { value: '', label: 'Select preferred timing' },
    { value: 'Afternoon (2 PM - 5 PM)', label: 'Afternoon (2 PM - 5 PM)' },
    { value: 'Evening (6 PM - 8 PM)', label: 'Evening (6 PM - 8 PM)' },
    { value: 'Weekend', label: 'Weekend' },
    { value: 'Flexible', label: 'Flexible' }
  ];

  const sourceOptions = [
    { value: '', label: 'How did you hear about us?' },
    { value: 'QR Code', label: 'QR Code' },
    { value: 'Social Media', label: 'Social Media' },
    { value: 'Google Search', label: 'Google Search' },
    { value: 'Friend/Family Referral', label: 'Friend/Family Referral' },
    { value: 'Advertisement', label: 'Advertisement' },
    { value: 'Walk-in', label: 'Walk-in' },
    { value: 'Other', label: 'Other' }
  ];

  const validateQRCode = useCallback(async () => {
    try {
      setLoading(true);
      await enquiryAPI.validateQRCode(qrCodeId);
      setQrValid(true);
    } catch (error) {
      setQrValid(false);
      setError('This QR code has expired or is invalid. Please contact us directly.');
    } finally {
      setLoading(false);
    }
  }, [qrCodeId]);

  useEffect(() => {
    if (qrCodeId) {
      validateQRCode();
    }
  }, [qrCodeId, validateQRCode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const requiredFields = ['first_name', 'last_name', 'email', 'phone'];
    for (const field of requiredFields) {
      if (!formData[field].trim()) {
        setError(`${field.replace('_', ' ').toUpperCase()} is required`);
        return false;
      }
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Basic phone validation
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(formData.phone.replace(/[\s\-()]/g, ''))) {
      setError('Please enter a valid phone number');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      
      if (qrCodeId) {
        // Submit via QR code
        await enquiryAPI.submitQRCodeEnquiry(qrCodeId, formData);
      } else {
        // Submit regular enquiry
        await enquiryAPI.create(formData);
      }
      
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      setError(error.response?.data?.detail || 'Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating QR code...</p>
        </div>
      </div>
    );
  }

  if (!qrValid && qrCodeId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaTimes className="text-2xl text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Invalid QR Code</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">Please contact us directly:</p>
            <p className="text-blue-600 font-medium">info@neuronschool.in</p>
            <p className="text-blue-600 font-medium">+91-9876543210</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-4 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaCheckCircle className="text-2xl text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            Your enquiry has been submitted successfully. We'll get back to you within 24 hours.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-2">What happens next?</h3>
            <ul className="text-sm text-blue-700 space-y-1 text-left">
              <li>• Check your email for our course brochure</li>
              <li>• Our team will contact you within 24 hours</li>
              <li>• Schedule a consultation to discuss your goals</li>
              <li>• Get personalized course recommendations</li>
            </ul>
          </div>
          <p className="text-sm text-gray-500">
            If you have any immediate questions, contact us at{' '}
            <a href="mailto:info@neuronschool.in" className="text-blue-600 hover:underline">
              info@neuronschool.in
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaGraduationCap className="text-3xl text-blue-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Course Enquiry Form
            </h1>
            <p className="text-gray-300">
              Tell us about your learning goals and we'll help you find the perfect course
            </p>
          </div>

          {/* Form */}
          <div className="bg-gray-800 rounded-lg shadow-lg p-6 md:p-8 border border-gray-700">
            {error && (
              <div className="bg-red-900 border border-red-700 rounded-lg p-4 mb-6">
                <p className="text-red-200">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <FaUser className="text-blue-400" />
                  Personal Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <FaEnvelope className="text-blue-400" />
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                      required
                    />
                  </div>
                </div>
              </div>



              {/* Course Information */}
              <div>
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <FaGraduationCap className="text-blue-400" />
                  Course Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Interested Course
                    </label>
                    <select
                      name="interested_course"
                      value={formData.interested_course}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                    >
                      {courseOptions.map(option => (
                        <option key={option.value} value={option.value} className="bg-gray-700 text-white">
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Preferred Batch Timing
                    </label>
                    <select
                      name="preferred_batch_timing"
                      value={formData.preferred_batch_timing}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                    >
                      {timingOptions.map(option => (
                        <option key={option.value} value={option.value} className="bg-gray-700 text-white">
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <FaInfoCircle className="text-blue-400" />
                  Additional Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      How did you hear about us?
                    </label>
                    <select
                      name="how_did_you_hear"
                      value={formData.how_did_you_hear}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                    >
                      {sourceOptions.map(option => (
                        <option key={option.value} value={option.value} className="bg-gray-700 text-white">
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      name="additional_notes"
                      value={formData.additional_notes}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Any additional information or specific requirements..."
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6 border-t border-gray-600">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                >
                  {submitting ? 'Submitting...' : 'Submit Enquiry'}
                </button>
                <p className="text-xs text-gray-400 text-center mt-3">
                  By submitting this form, you agree to receive communications from us about our courses and services.
                </p>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-gray-400">
              Need immediate assistance? Contact us at{' '}
              <a href="mailto:info@neuronschool.in" className="text-blue-400 hover:underline">
                info@neuronschool.in
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicEnquiryForm; 